if (!customElements.get('cart-pre')) {
  customElements.define(
    'cart-pre',
    class CartPre extends HTMLElement {
      constructor() {
        super();

        this.cartToken = undefined;

        /**
         * @type {'no_account' | 'existing_prescriptions' | 'upload' | 'action' | 'upload_later' | 'loading'}
         */
        this.state = 'loading';

        /**
         * @type {NodeListOf<HTMLElement>}
         */
        this.stateContainers = this.querySelectorAll('[data-state]');

        /**
         * @type {boolean}
         */
        this.hasAccount = this.getAttribute('has-account') === 'true' ? true : false;
        this.uploadButton = this.querySelectorAll('[data-action="upload"]');
        this.uploadLaterButton = this.querySelector('[data-action="later"]');

        /**
         * @type {HTMLElement | null}
         */
        this.uploadContainer = this.querySelector('.cart-pre-upload-form');

        /**
         * @type {HTMLFormElement | null}
         */
        this.uploadForm = this.querySelector('.cart-pre-upload-form__form');
        this.submitButton = this.uploadForm?.querySelector('[type="submit"]');
        this.fileInput = this.querySelector('input[type="file"]');
        this.fileUploadContinueButton = this.querySelector('.cart-pre-upload-form__submit-button');
        this.filename = this.querySelectorAll('[data-prescription-form-filename]');

        /**
         * @type {HTMLElement | null}
         */
        this.noAccountContainer = this.querySelector('.cart-pre-no-account');

        /**
         * @type {HTMLElement | null}
         */
        this.uploadLaterContainer = this.querySelector('.cart-pre-upload-later');

        /**
         * Existing prescriptions
         */
        this.existingPrescription = null;
        this.existingPrescriptionsContainer = this.querySelector('.cart-pre-upload-form__existing-prescriptions');
        this.prescriptionsAccordion = this.querySelector('.cart-pre-upload-form__existing-prescriptions-accordion');
        this.existingPrescriptionsSubmit = this.querySelector('.cart-pre-upload-form__existing-prescriptions-submit');

        this.fileInput?.addEventListener('change', this.handleFileInput.bind(this));
        this.fileUploadContinueButton?.addEventListener('click', this.formSubmitHandler.bind(this));

        this.uploadButton?.forEach((button) => button.addEventListener('click', (e) => this.uploadButtonHandler(e)));
        this.uploadLaterButton?.addEventListener('click', () => this.uploadLaterButtonHandler());
        this.uploadForm?.addEventListener('submit', (e) => this.formSubmitHandler(e));
        this.existingPrescriptionsSubmit?.addEventListener('click', (e) => this.existingPrescriptionSubmitHandler(e));

        this.fetchData();
      }

      onStateChange() {
        const containers = Array.from(this.stateContainers);

        containers.forEach((container) => {
          const state = container.getAttribute('data-state');

          container.style.display = state === this.state ? 'block' : 'none';
        });

        if (this.state === 'no_account' || this.state === 'upload' || this.state === 'upload_later') {
          const uploadState = containers.filter((container) => container.getAttribute('data-state') === 'action');

          uploadState.forEach((container) => {
            container.style.display = 'block';
          });
        }

        this.setAttribute('data-current-state', this.state);
      }

      async fetchData() {
        this.getCartToken().then((token) => {
          this.cartToken = token.split('?')[0];
        });

        this.state = 'action';
        this.onStateChange();
      }

      async fetchPrescriptions() {
        if (!window.CubittsAPI || !window.theme.customer.id) return;

        const response = await window.CubittsAPI.getCustomerPrescriptions(window.theme.customer.id);
        const prescriptions = this.filterPrescriptions(response);
        prescriptions.forEach((prescription, index) => {
          if (index > 0) return;
          this.existingPrescription = prescription;
          const accordionClone = this.prescriptionsAccordion.cloneNode(true);
          this.renderPrescription(prescription, accordionClone);
          this.existingPrescriptionsContainer.appendChild(accordionClone);
        });
        this.prescriptionsAccordion.remove();
        return prescriptions;
      }

      filterPrescriptions(response) {
        return response.filter((prescription) => prescription.date);
      }

      /**
       * @param {Date} date
       */
      formatDate(date) {
        const dateFormatter = Intl.DateTimeFormat('en-gb', {
          day: 'numeric',
          month: 'short',
          year: 'numeric',
        });
        const dateParts = dateFormatter.formatToParts(date);
        const day = dateParts.find((part) => part.type === 'day')?.value;
        const month = dateParts.find((part) => part.type === 'month')?.value;
        const year = dateParts.find((part) => part.type === 'year')?.value;

        return `${day}${this.getOrdinalForDay(day)} ${month} ${year}`;
      }

      /**
       * @param {Number} day
       */
      getOrdinalForDay(day) {
        if (day > 3 && day < 21) return 'th';
        switch (day % 10) {
          case 1:
            return 'st';
          case 2:
            return 'nd';
          case 3:
            return 'rd';
          default:
            return 'th';
        }
      }

      /**
       * @param {Object} prescription
       * @param {HTMLElement} accordion
       */
      renderPrescription(prescription, accordion) {
        const title = accordion.querySelector('.accordion__title span');
        const expiryDate = accordion.querySelector('.account-prescriptions__expiry-date-content');
        const sphLeft = accordion.querySelector('.account-prescriptions__sph-l');
        const sphRight = accordion.querySelector('.account-prescriptions__sph-r');
        const cylLeft = accordion.querySelector('.account-prescriptions__cyl-l');
        const cylRight = accordion.querySelector('.account-prescriptions__cyl-r');
        const axisLeft = accordion.querySelector('.account-prescriptions__axis-l');
        const axisRight = accordion.querySelector('.account-prescriptions__axis-r');
        const intAddLeft = accordion.querySelector('.account-prescriptions__int-add-l');
        const intAddRight = accordion.querySelector('.account-prescriptions__int-add-r');
        const nearAddLeft = accordion.querySelector('.account-prescriptions__near-add-l');
        const nearAddRight = accordion.querySelector('.account-prescriptions__near-add-r');
        const prismLeft = accordion.querySelector('.account-prescriptions__prism-l');
        const prismRight = accordion.querySelector('.account-prescriptions__prism-r');
        const pdLeft = accordion.querySelector('.account-prescriptions__pd-l');
        const pdRight = accordion.querySelector('.account-prescriptions__pd-r');

        if (title) title.textContent = this.formatDate(new Date(prescription.date));
        if (expiryDate) expiryDate.textContent = this.formatDate(new Date(prescription.recall));
        if (sphLeft) sphLeft.textContent = prescription.left_eye_sphere;
        if (sphRight) sphRight.textContent = prescription.right_eye_sphere;
        if (axisLeft) axisLeft.textContent = prescription.left_eye_axis;
        if (axisRight) axisRight.textContent = prescription.right_eye_axis;
        if (cylLeft) cylLeft.textContent = prescription.left_eye_cylinder;
        if (cylRight) cylRight.textContent = prescription.right_eye_cylinder;
        if (intAddLeft) intAddLeft.textContent = prescription.left_eye_int_add;
        if (intAddRight) intAddRight.textContent = prescription.right_eye_int_add;
        if (nearAddLeft) nearAddLeft.textContent = prescription.left_eye_near_add;
        if (nearAddRight) nearAddRight.textContent = prescription.right_eye_near_add;
        if (prismLeft) prismLeft.textContent = prescription.left_eye_prism;
        if (prismRight) prismRight.textContent = prescription.right_eye_prism;
        if (pdLeft) pdLeft.textContent = prescription.left_eye_pupillary_distance;
        if (pdRight) pdRight.textContent = prescription.right_eye_pupillary_distance;
      }

      getCartToken = async () => {
        try {
          const response = await window.Cart.cartToken();

          return response.token;
        } catch (e) {
          return;
        }
      };

      getBase64 = (file) => {
        return new Promise((resolve, reject) => {
          const reader = new FileReader();
          reader.readAsDataURL(file);

          reader.onload = () => resolve(reader.result);
          reader.onerror = (error) => reject(error);
        });
      };

      /**
       *
       * @param {Event} e
       */
      async formSubmitHandler(e) {
        e.preventDefault();
        if (!this.uploadForm) {
          return;
        }
        const formData = new FormData(this.uploadForm);
        const image = formData.get('image');
        const distance = formData.get('distance');
        const base64 = image ? await this.getBase64(image) : false;
        const body = {
          __cartTokenId: this.cartToken,
          image: base64,
          customerId: window.theme.customer.id,
        };

        if (distance) {
          fetch(window.Shopify.routes.root + 'cart/update.js', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json'
            },
            body: JSON.stringify({attributes: {
              'distance': distance
            }})
          })
          .then(response => {
            return response.json();
          })
          .catch((error) => {
            console.error('Error:', error);
          });
        }

        if (this.fileUploadContinueButton) {
          this.fileUploadContinueButton?.setAttribute('disabled', 'disabled');
          this.fileUploadContinueButton?.classList.add('button--disabled');
          const loadingLabel = this.fileUploadContinueButton?.getAttribute('data-loading-label') || 'Uploading...';
          this.fileUploadContinueButton.innerHTML = loadingLabel;
        }

        try {
          const response = await window.CubittsAPI?.prePurchasePrescriptionImageUpload(body);
          console.log('response', response);
        } catch (e) {
          console.error('error', e);
        }

        this.querySelector('.prescription-form__upload-step--file')?.classList.add('util-hidden');
        this.querySelector('[data-state="action"]')?.classList.add('util-hidden');
        this.querySelector('.prescription-form__upload-step--complete')?.classList.remove('util-hidden');
      }

      /**
       *
       * @param {Event} e
       */
      handleFileInput(e) {
        if (e.target) {
          const file = e.target.files[0];
          const filename = file.name;

          this.filename.forEach((el) => {
            el.classList.remove('util-hidden');
            el.textContent = filename;
          });

          if (this.fileUploadContinueButton) {
            this.fileUploadContinueButton.removeAttribute('disabled');
            this.fileUploadContinueButton.classList.remove('button--disabled');
          }
        }
      }

      /**
       *
       * @param {Event} e
       */
      handleFileInputContinue(e) {
        e.preventDefault();
        this.querySelector('.prescription-form__upload-step--file')?.classList.add('util-hidden');
        this.querySelector('.prescription-form__upload-step--distance')?.classList.remove('util-hidden');
        // if (this.distanceRadioChecked()) {
        //   this.enableSubmitButton();
        // }
      }

      existingPrescriptionSubmitHandler(e) {
        e.preventDefault();

        if (!this.existingPrescription || !window.CubittsAPI) {
          return;
        }

        window.CubittsAPI.postPurchaseSendExisting({
          __cartTokenId: this.cartToken,
          prescriptionType: 'selected',
          prescriptionId: this.existingPrescription.id,
        });
      }

      uploadButtonHandler(e) {
        e.preventDefault();
        this.state = 'upload';
        this.onStateChange();
        this.toggleNoAccountContainer();
        if (this.uploadLaterContainer) {
          this.toggleElement(this.uploadLaterContainer, true);
        }
      }

      uploadLaterButtonHandler() {
        this.toggleUploadLaterContainer();
        this.state = 'upload_later';
        this.onStateChange();
        const formContainer = this.hasAccount ? this.uploadContainer : this.noAccountContainer;
        if (formContainer) {
          this.toggleElement(formContainer, true);
        }
      }

      /**
       *
       * @param {HTMLElement} element
       */
      toggleElement(element, forceHide = false) {
        if (forceHide) {
          element.style.display = 'none';
          return;
        }

        element.style.display = element.style.display === 'block' ? 'none' : 'block';
      }

      toggleNoAccountContainer() {
        const container = this.hasAccount ? this.uploadContainer : this.noAccountContainer;
        if (container) {
          this.toggleElement(container);
        }
      }

      toggleUploadLaterContainer() {
        if (!this.uploadLaterContainer) {
          return;
        }

        this.toggleElement(this.uploadLaterContainer);
      }
    },
  );
}
